/**
 * The kitchen side of an offline send (06-flows.md F10; 03-modules.md §5): from the order
 * the till just sent, the KDS ticket and the KOT print data it shows and prints itself,
 * because the cloud's kitchen module cannot do it while the cloud is away.
 *
 * Routing: the bootstrap bundle carries stations but not the routing rules, so offline
 * every item goes to the outlet's default station (the same one the server uses for items
 * no rule names). Per-category routing offline needs the rules in the bundle (pending).
 */
import type { OrderView, OutletBootstrap } from '@qrkey/api-client';
import { formatOutletTime, type KotPrintData } from '@qrkey/domain';
import type { HubTicketInput } from '@qrkey/outlet-hub/protocol';

type Station = OutletBootstrap['stations'][number];

export function defaultStation(bootstrap: Pick<OutletBootstrap, 'stations'>): Station | null {
  const live = bootstrap.stations.filter((s) => s.archivedAt === null);
  return live.find((s) => s.isDefault) ?? live[0] ?? null;
}

export interface SendFacts {
  readonly order: OrderView;
  readonly kotId: string;
  /** The number printed from the terminal's range, when it had one. */
  readonly kotNo: number | null;
  readonly bootstrap: OutletBootstrap;
  readonly sentBy: string | null;
  readonly userId: string | null;
  readonly now: Date;
}

/** The lines this send put on the KOT (`kotId` is set on them by the local engine). */
function sentLines(order: OrderView, kotId: string) {
  return order.lines.filter((l) => l.kotId === kotId && l.status !== 'void');
}

function tableNames(order: OrderView, bootstrap: OutletBootstrap): string[] {
  return order.tableIds.flatMap((id) => {
    const t = bootstrap.tables.find((x) => x.id === id);
    return t ? [t.name] : [];
  });
}

/** The KDS ticket of the send, or null when nothing was sent or no station can show it. */
export function buildTicket(f: SendFacts): HubTicketInput | null {
  const station = defaultStation(f.bootstrap);
  const lines = sentLines(f.order, f.kotId);
  if (!station || !station.kdsEnabled || lines.length === 0) return null;
  const earlier = f.order.lines.some((l) => l.kotId !== null && l.kotId !== f.kotId);
  return {
    id: f.kotId,
    outletId: f.order.outletId,
    orderId: f.order.id,
    orderNo: f.order.orderNo,
    businessDate: f.order.businessDate,
    ...(f.kotNo !== null ? { kotNo: f.kotNo } : {}),
    stationId: station.id,
    stationName: station.name,
    kind: earlier ? 'incremental' : 'new',
    copy: false,
    requestId: null,
    reprintOfKotId: null,
    status: 'displayed',
    failureReason: null,
    orderType: f.order.type,
    tableNames: tableNames(f.order, f.bootstrap),
    sourceLabel: null,
    packagingNote: null,
    sentBy: f.sentBy,
    voidReason: null,
    createdBy: f.userId,
    createdAt: f.now.toISOString(),
    printedAt: null,
    bumpedAt: null,
    prepMinutes: null,
    lines: lines.map((l) => ({
      orderLineId: l.id,
      productId: l.productId,
      name: l.name,
      qtyMilli: l.qtyMilli,
      note: l.note,
      seat: l.seat,
      course: l.course,
      modifiers: l.modifiers.map((m) => ({ kind: m.kind, name: m.name, qtyMilli: m.qtyMilli })),
      isNew: true,
      prepMinutes: null,
      status: 'sent',
    })),
  } as HubTicketInput;
}

/** What the station's printers print for the send. One document per printer (its copies). */
export function buildKotPrints(
  f: SendFacts,
  kotNo: number,
): { printerId: string; copies: number; data: KotPrintData }[] {
  const station = defaultStation(f.bootstrap);
  const lines = sentLines(f.order, f.kotId);
  if (!station || lines.length === 0) return [];
  const earlier = f.order.lines.some((l) => l.kotId !== null && l.kotId !== f.kotId);
  const zone = f.bootstrap.outlet.timezone;
  return station.printers.flatMap((sp) => {
    const printer = f.bootstrap.printers.find(
      (p) => p.id === sp.printerId && p.archivedAt === null,
    );
    if (!printer) return [];
    return [
      {
        printerId: printer.id,
        copies: sp.copies,
        data: {
          outletName: f.bootstrap.outlet.name,
          stationName: station.name,
          kotNo,
          kind: earlier ? ('incremental' as const) : ('new' as const),
          copy: false,
          orderNo: f.order.orderNo,
          orderType: f.order.type,
          tableNames: tableNames(f.order, f.bootstrap),
          at: formatOutletTime(f.now, zone),
          sentBy: f.sentBy,
          lines: lines.map((l) => ({
            orderLineId: l.id,
            productId: l.productId,
            name: l.name,
            qtyMilli: l.qtyMilli,
            note: l.note,
            seat: l.seat,
            course: l.course,
            modifiers: l.modifiers.map((m) => ({
              kind: m.kind,
              name: m.name,
              qtyMilli: m.qtyMilli,
            })),
            prepMinutes: null,
            isNew: true,
          })),
          paperMm: printer.paperMm,
          copies: sp.copies,
          watermark: null,
        },
      },
    ];
  });
}
